import { Button, Card, CardContent } from '@atreus/ui';
import { LogIn } from 'lucide-react';
import { useState } from 'react';

import { saveSession, type AdminSession } from '../auth/session.js';
import { ApiError, apiPost } from '../cloud.js';

interface LoginResponse {
  accessToken: string;
  accessExpiresAt: number;
  refreshToken: string;
  tenant: { id: string };
  user: { id: string; email: string; role: string };
}

export const LoginPage = ({ onLogin }: { onLogin: () => void }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending) return;
    setError(null);
    setPending(true);
    try {
      const resp = await apiPost<LoginResponse>('/auth/login', {
        email: email.trim(),
        password,
      });
      if (resp.user.role !== 'superadmin') {
        setError('Esta cuenta no tiene permisos de super-admin.');
        setPending(false);
        return;
      }
      const session: AdminSession = {
        accessToken: resp.accessToken,
        accessExpiresAt: resp.accessExpiresAt,
        refreshToken: resp.refreshToken,
        user: { id: resp.user.id, email: resp.user.email, role: 'superadmin' },
        tenantId: resp.tenant.id,
        savedAt: Date.now(),
      };
      saveSession(session);
      onLogin();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Email o contraseña incorrectos.');
      } else {
        setError((err as Error).message);
      }
      setPending(false);
    }
  };

  const disabled = pending || email.trim() === '' || password === '';

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-md p-6">
        <div className="mb-8 flex justify-center">
          <img
            src="/atreus-logo-stacked.svg"
            alt="Atreus"
            className="h-32 w-auto dark:invert"
          />
        </div>

        <Card>
          <CardContent className="pt-6">
            <h1 className="text-2xl font-black tracking-tight">Iniciar sesión</h1>
            <p className="mt-1 text-sm text-fg-muted">
              Acceso restringido a super-admin.
            </p>

            <form
              onSubmit={submit}
              autoComplete="off"
              className="mt-6 space-y-4"
            >
              <div>
                <label
                  htmlFor="login-email"
                  className="mb-1 block text-sm font-semibold"
                >
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="off"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={pending}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
                />
              </div>
              <div>
                <label
                  htmlFor="login-password"
                  className="mb-1 block text-sm font-semibold"
                >
                  Contraseña
                </label>
                <input
                  id="login-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={pending}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
                />
              </div>

              {error ? <p className="text-sm text-danger">{error}</p> : null}

              <Button type="submit" size="lg" className="w-full" disabled={disabled}>
                <LogIn className="size-4" />
                {pending ? 'Entrando…' : 'Entrar'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
